/// <reference path="../../typings/browser.d.ts" />

/**
 * Network layer service
 */
namespace Application.Services {
    'use strict';

    import ITopologyElement = Models.ITopologyElement;
    import INetworkNode = Models.INetworkNode;
    import INetwork = Models.INetwork;
    import IGateway = Models.IGateway;
    import IProbe = Models.IProbe;
    import ILink = Models.ILink;
    import IHotspot = Models.IHotspot;
    import IAllTopologyElements = Models.IAllTopologyElements;
    import TopologyElementType = Models.TopologyElementType;

    /**
     * NetworkNodeTypeService
     */
    export class TopologyElementService {
        public static $name: string = "topologyElementService";
        public static $inject: string[] = ['$q',
                                           'backendService',
                                           'networkNodeService',
                                           'networkNodeTypeService',
                                           'networkService',
                                           'gatewayService',
                                           'linkService',
                                           'probeService',
                                           'hotspotService',
                                           'multiBlockService',
                                           'sonAdapterService',
                                           ];

        private backendBaseUrl: string = window.backendUrl;
        // private servicePath: string = 'networkElement/';
        private servicePath: string = '';
        private backendUrl: string = this.backendBaseUrl + this.servicePath + 'topologyElements';
        public static INITIAL_POSITION: number = 0;

        constructor(private $q: ng.IQService,
                    private backendService: BackendService,
                    private networkNodeService: NetworkNodeService,
                    private networkNodeTypeService: NetworkNodeTypeService,
                    private networkService: NetworkService,
                    private gatewayService: GatewayService,
                    private linkService: Services.LinkService,
                    private probeService: ProbeService,
                    private hotspotService: HotspotService,
                    private multiBlockService: Services.MultiBlockService,
                    private sonAdapterService: SonAdapterService) {
        }

        public setNodeTypeName(element: Models.INetworkNode): void {
            if (element !== undefined) {
                this.networkNodeTypeService
                    .findByHref(element.type.href)
                    .then((networkNodeType: Models.INetworkNodeType) => {
                        element.type = networkNodeType;
                    });
            }
        }

        public getAllTopologyElements(): ng.IPromise<IAllTopologyElements> {
            return this.$q.all([
                this.getNetworkNodeElements(),
                this.networkService.getAllNetworksPromise(),
                this.getGatewayElements(),
                this.linkService.getAllLinks(),
                this.getProbeElements(),
                // this.getHotspotElements(),
                this.getAllMultiBlocks(),
            ]).then((allValues: ITopologyElement[][]) => {
                let response: IAllTopologyElements = {
                    networkNodes: allValues[0] as Models.INetworkNode[],
                    networks: allValues[1] as Models.INetwork[],
                    gateways: allValues[2] as Models.IGateway[],
                    links: allValues[3] as Models.ILink[],
                    probes: allValues[4] as Models.IProbe[],
                    // removing hotspots requests since they are not being used
                    // hotspots: allValues[5] as Models.IHotspot[],
                    // multiblocks: allValues[6] as Models.MultiBlock[],
                    hotspots: [],
                    multiblocks: allValues[5] as Models.MultiBlock[],
                };
                return this.sonAdapterService.getSonPacketmap().then((res: Models.ISonPacketmap) => {
                    response.sonNodes = res.switches;
                    response.sonLinks = res.links;
                    return response;
                }).catch((reason: ngCustom.IReason) => {
                    return response;
                });
            });
        }

        public getNetworkNodeElements(): ng.IPromise<INetworkNode[]> {
            return this.networkNodeService.getAllEnabledNetworkNodesPromise()
                .then((networkNodes: Models.INetworkNode[]) => {
                    // this.nodeHealthService.registerCallbackFor(networkNodes);
                    return networkNodes;
                });
        }

        public getNetworkNodeElementById(id: number): ng.IPromise<INetworkNode> {
            return this.networkNodeService.getByIdPromise(id).then((n: INetworkNode) => {
                return n;
            });
        }

        public getGatewayElements(): ng.IPromise<IGateway[]> {
            return this.gatewayService.getAllGatewaysPromise()
                .then((gateways: Models.IGateway[]) => {
                    // this.nodeHealthService.registerCallbackFor(gateways);
                    return gateways;
                });
        }

        public getGatewayElementById(id: number): ng.IPromise<IGateway> {
            return this.gatewayService.getById(id).then((g: IGateway) => {
                return g;
            });
        }

        public getProbeElements(): ng.IPromise<IProbe[]> {
            return this.probeService.getAllProbesPromise()
                .then((probes: Models.IProbe[]) => {
                    return probes;
                });
        }

        public getProbeElementById(id: number): ng.IPromise<IProbe> {
            return this.probeService.getProbePromiseById(id).then((p: IProbe) => {
                return p;
            });
        }

        public getHotspotElements(): ng.IPromise<IHotspot[]> {
            return this.hotspotService.getAllHotspotsPromise()
                .then((hotspots: Models.IHotspot[]) => {
                    return hotspots;
                });
        }

        public getHotspotElementById(id: number): ng.IPromise<IHotspot> {
            return this.hotspotService.getHotspotPromiseById(id).then((h: IHotspot) => {
                return h;
            });
        }

        public getLinkElements(): ng.IPromise<ILink[]> {
            return this.linkService.getAllLinks().
                then((links: Models.ILink[]) => {
                    return links;
                });
        }

        public getLinkElementById(id: number): ng.IPromise<ILink> {
            return this.linkService.getLinkById(id).then((l: ILink) => {
                return l;
            });
        }

        public getNetworkElementById(id: number): ng.IPromise<Models.Network> {
            return this.networkService.getNetworkById(id);
        }

        public getAllMultiBlocks(): ng.IPromise<Models.MultiBlock[]> {
            return this.multiBlockService.getAllMultiBlocks().then((m: Models.MultiBlock[]) => {
                return m;
            });
        }

        public saveTopologyElement(element: ITopologyElement): ng.IPromise<ITopologyElement> {
            if (element) {
                if (element.class() === TopologyElementType.NetworkNode)
                    return this.networkNodeService.saveNetworkNode(element as INetworkNode);
                else if (element.class() === TopologyElementType.Network)
                    return this.networkService.saveNetwork(element as INetwork);
                else if (element.class() === TopologyElementType.Gateway)
                    return this.gatewayService.saveGateway(element as IGateway);
                else if (element.class() === TopologyElementType.Link)
                    return this.linkService.saveLink(element as ILink);
                else if (element.class() === TopologyElementType.Probe)
                    return this.probeService.saveProbe(element as IProbe);
                else if (element.class() === TopologyElementType.Hotspot)
                    return this.hotspotService.saveHotspot(element as IHotspot);
            }
        }

        public placeTopologyElement(element: ITopologyElement): ng.IPromise<ITopologyElement> {
            let simplifiedHref: string = Common.Utils.getSimplifiedHrefFromString(element.href);
            return this.backendService.put(this.backendUrl + "/place" + simplifiedHref, element)
                .then((result: ng.IHttpPromiseCallbackArg<Models.ITopologyElement>) => {
                    return element;
            });
        }

        public createTopologyElement(elementType: TopologyElementType, element: ITopologyElement): ng.IPromise<ITopologyElement> {
            if (elementType === TopologyElementType.NetworkNode) {
                if (element.href && element.href !== '') {
                    const [x, y] = this.initTopologyCoordinate();
                    return this.networkNodeService.addPositionToNode(element.href, x, y);
                }
            } else if (elementType === TopologyElementType.Network) {
                let network: Models.INetwork = element as Models.INetwork;
                [network.x, network.y] = this.initTopologyCoordinate();

                return this.networkService.saveNetwork(network);
            } else if (elementType === TopologyElementType.Gateway) {
                let gateway: Models.IGateway = element as Models.Gateway;
                gateway.natEnabled = true;
                gateway.health = {
                    data: {
                        healthStatus: 'PENDING',
                        checks: [],
                        elementId: '',
                    },
                };
                [gateway.x, gateway.y] = this.initTopologyCoordinate();
                return this.gatewayService.saveGateway(gateway);
            } else if (elementType === TopologyElementType.Probe) {
                let probe: Models.IProbe = Models.Probe.Empty();
                probe.href = element.href;
                probe.name = element.name;
                probe.networkInterfaces = (element as Models.Probe).networkInterfaces;
                [probe.x, probe.y] = this.initTopologyCoordinate();
                return this.probeService.saveProbe(probe);
            }  else if (elementType === TopologyElementType.Hotspot) {
                let hotspot: Models.Hotspot = element as Models.Hotspot;
                [hotspot.x, hotspot.y] = this.initTopologyCoordinate();
                return this.hotspotService.saveHotspot(hotspot);
            }
        }

        public deleteTopologyElement(element: ITopologyElement): ng.IPromise<ITopologyElement> {
            if (element) {
                // if the element is a NetworkNode, only x and y are deleted
                if (element.class() === TopologyElementType.NetworkNode) {
                    return this.networkNodeService.addPositionToNode(element.href, undefined, undefined);
                } else if (element.class() === TopologyElementType.Network) {
                    return this.networkService.deleteNetwork(element as INetwork)
                    .then(() => {return undefined; });
                } else if (element.class() === TopologyElementType.Gateway) {
                    return this.gatewayService.deleteGateway(element as IGateway)
                    .then(() => {return undefined; });
                } else if (element.class() === TopologyElementType.Link) {
                    return this.linkService.deleteLink(element as ILink)
                    .then(() => {return undefined; });
                } else if (element.class() === TopologyElementType.Probe) {
                    return this.probeService.addPositionToProbe(element.href, undefined, undefined);
                } else if (element.class() === TopologyElementType.Hotspot) {
                    return this.hotspotService.deleteHotspot(element as IHotspot);
                }
            }
        }

        public getTopologyElement(element: ITopologyElement): ng.IPromise<ITopologyElement> {
            if (element) {
                // if the element is a NetworkNode, only x and y are deleted
                if (element.href.indexOf('networkNodes/') !== -1) {
                    return this.networkNodeService.getNetworkNodePromise(element.href).then((r: Models.INetworkNode) => { return r; });
                } else if (element.href.indexOf('networks/') !== -1) {
                    return this.networkService.getNetworkPromise(element.href).then((r: Models.INetwork) => { return r; });
                } else if (element.href.indexOf('gateways/') !== -1) {
                    return this.gatewayService.getGatewayPromise(element.href).then((r: Models.IGateway) => { return r; });
                } else if (element.href.indexOf('links/') !== -1) {
                    return this.linkService.getLink(element.href).then((r: Models.ILink) => { return r; });
                } else if (element.href.indexOf('probes/') !== -1) {
                    return this.probeService.getProbePromise(element.href).then((r: Models.IProbe) => { return r; });
                } else if (element.href.indexOf('hotspots/') !== -1) {
                    return this.hotspotService.getHotspotPromise(element.href).then((r: Models.IHotspot) => { return r; });
                }
            }
        }

        public convertSimplifiedHrefToHref(simplifiedHref: string): string {
            return this.backendBaseUrl + simplifiedHref;
        }

        public searchByText(text: string): ng.IPromise<Models.ITopologySearchResult[]> {
            let url: string = this.backendUrl + "/search/byText?text=" + text;
            return this.backendService.get(url)
                .then((result: ng.IHttpPromiseCallbackArg<Models.ITopologySearchResult[]>) => {
                    return result.data;
            });
        }

        public initTopologyCoordinate(): number[] {
            return [TopologyElementService.INITIAL_POSITION, TopologyElementService.INITIAL_POSITION];
        }

        public isNodeCoordinateDefined(node: ITopologyElement): boolean {
            return node.x != undefined && node.y != undefined;
        }
    }
}
